import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Alert,
  Platform,
  StyleSheet as RNStyleSheet,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { CameraView, useCameraPermissions } from 'expo-camera';
import PantallaSegura from '../components/PantallaSegura';
import ScreenBackground from '../components/ScreenBackground';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { storageService, resizeToAspect } from '../services/storageService';
import { callAgregarVehiculo } from '../services/cloudFunctions';
import { ayudaPlaca, normalizarPlaca, revisarPlaca } from '../utils/placa';
import { mensajeDeError } from '../utils/mensajeDeError';

type Tipo = 'CAR' | 'MOTORCYCLE' | 'MINIBUS' | 'BICYCLE';

const TIPOS: { id: Tipo; nombre: string; icono: any; cuota: string }[] = [
  { id: 'CAR', nombre: 'Carro', icono: 'car-sport', cuota: 'RD$1,300 al mes' },
  { id: 'MOTORCYCLE', nombre: 'Motor', icono: 'bicycle', cuota: 'RD$1,000 al mes' },
  { id: 'MINIBUS', nombre: 'Minibús', icono: 'bus', cuota: 'RD$1,500 al mes' },
];

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AGREGAR UN VEHÍCULO — con su propio proceso
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo que pidió el fundador, literal: «para eso debe enviarme una foto de la
 * parte de atrás del carro donde pueda ver color y el número de placa, y una
 * selfie en tiempo real para saber que el cambio lo está haciendo el mismo
 * chofer. Mi app no puede tener perfil creado por otro: siempre será el mismo
 * titular de la cuenta quien debe conducir.»
 *
 * ── POR QUÉ DOS CÁMARAS DISTINTAS ────────────────────────────────────────
 *
 *  · LA PLACA va con la CÁMARA DEL SISTEMA (`launchCameraAsync`), igual que la
 *    cédula en kyc-documents.tsx. Captura a la resolución completa del sensor
 *    y deja encuadrar con calma: la placa tiene que poderse LEER, y es el único
 *    dato que el pasajero comprueba con sus propios ojos en la acera. Se abre
 *    la cámara, no la galería: una foto vieja no prueba nada.
 *
 *  · LA SELFIE va con `CameraView` frontal dentro de la app, como en
 *    kyc-biometrics.tsx. Aquí lo que importa no es el detalle sino que la foto
 *    se tome AHORA y de frente, que es lo que convierte esto en una
 *    comprobación de que el cambio lo hace el titular.
 *
 * ── EL AJUSTE DE TAMAÑO NO ES COSMÉTICO ──────────────────────────────────
 * `takePictureAsync` captura a resolución completa: en un móvil de 48 MP eso
 * pasa de los topes de Storage con facilidad y la subida falla SIEMPRE, con el
 * mismo error para todos los intentos. Ya nos dejó a un usuario atrapado en el
 * primer paso del registro. Se reutiliza `resizeToAspect` del storageService.
 * La selfie va a 9:16 (reducir sin recortar: un recorte le quitaría la frente
 * justo en la foto que alguien tiene que comparar contra la cédula) y la placa
 * a 4:3 con 1600 de ancho, que deja los números legibles.
 */
export default function AgregarVehiculoScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const uid = useAppStore((s) => s.userFirebaseUid);

  const [tipo, setTipo] = React.useState<Tipo | null>(null);
  const [marca, setMarca] = React.useState('');
  const [modelo, setModelo] = React.useState('');
  const [color, setColor] = React.useState('');
  const [anio, setAnio] = React.useState('');
  const [placa, setPlaca] = React.useState('');

  const [fotoTrasera, setFotoTrasera] = React.useState<string | null>(null);
  const [selfie, setSelfie] = React.useState<string | null>(null);

  const [camaraAbierta, setCamaraAbierta] = React.useState(false);
  const [camaraRef, setCamaraRef] = React.useState<any>(null);
  const [capturando, setCapturando] = React.useState(false);
  const [permiso, pedirPermiso] = useCameraPermissions();

  const [enviando, setEnviando] = React.useState(false);

  const esMoto = tipo === 'MOTORCYCLE' || tipo === 'BICYCLE';

  const avisar = (titulo: string, texto: string) => {
    // En react-native-web `Alert.alert` no pinta nada y el botón parece muerto.
    if (Platform.OS === 'web') window.alert(`${titulo}\n\n${texto}`);
    else Alert.alert(titulo, texto);
  };

  // ── LA FOTO DE LA PLACA ──────────────────────────────────────────────────
  const tomarFotoTrasera = async () => {
    try {
      if (Platform.OS !== 'web') {
        const { status } = await ImagePicker.requestCameraPermissionsAsync();
        if (status !== 'granted') {
          avisar(
            'Permiso denegado',
            'Necesitamos la cámara para que tomes la foto de la parte de atrás de tu vehículo en este momento.'
          );
          return;
        }
      }
      // En web no existe la cámara del sistema; se cae a la galería para poder
      // probar la pantalla desde el navegador. En el teléfono siempre cámara.
      const abrir = Platform.OS === 'web' ? ImagePicker.launchImageLibraryAsync : ImagePicker.launchCameraAsync;
      const r = await abrir({ mediaTypes: ['images'], quality: 0.85 });
      if (r.canceled || !r.assets?.[0]?.uri) return;
      const ajustada = await resizeToAspect(r.assets[0].uri, 1600, 4, 3);
      setFotoTrasera(ajustada);
    } catch (e: any) {
      avisar('No pudimos tomar la foto', mensajeDeError(e, 'Inténtalo otra vez.'));
    }
  };

  // ── LA SELFIE EN VIVO ────────────────────────────────────────────────────
  const abrirCamara = async () => {
    if (!permiso?.granted) {
      const r = await pedirPermiso();
      if (!r?.granted) {
        avisar(
          'Permiso denegado',
          'Necesitamos la cámara para tomar tu selfie en este momento y comprobar que el cambio lo haces tú.'
        );
        return;
      }
    }
    setCamaraAbierta(true);
  };

  const tomarSelfie = async () => {
    if (capturando) return;
    // Sin este aviso el obturador no hacía NADA en silencio mientras la
    // referencia de la cámara no hubiera llegado (mismo fallo que ya costó un
    // rechazo de Play por "botones que no responden").
    if (!camaraRef) {
      avisar('La cámara aún no está lista', 'Espera un segundo a verte en pantalla y vuelve a pulsar.');
      return;
    }
    setCapturando(true);
    try {
      const foto = await camaraRef.takePictureAsync({ quality: 0.8 });
      if (!foto?.uri) throw new Error('la cámara no devolvió ninguna foto');
      setCamaraAbierta(false);
      const ajustada = await resizeToAspect(foto.uri, 1080, 9, 16);
      setSelfie(ajustada);
    } catch (e: any) {
      // La cámara se CIERRA: dejarla abierta tras un fallo parece que la app se
      // colgó.
      setCamaraAbierta(false);
      avisar(
        'No pudimos tomar la foto',
        'Cierra otras apps que estén usando la cámara y vuelve a intentarlo.'
      );
    } finally {
      setCapturando(false);
    }
  };

  const puedeEnviar =
    !!tipo && !!marca.trim() && !!modelo.trim() && !!color.trim() && !!placa && !!fotoTrasera && !!selfie;

  const enviar = async () => {
    if (enviando || !uid) return;
    const revision = revisarPlaca(placa, esMoto);
    if (!revision.ok) {
      avisar(revision.titulo, revision.motivo);
      return;
    }
    setEnviando(true);
    try {
      // Las fotos suben primero a la carpeta del propio chofer; el servidor las
      // valida y las CONGELA en una carpeta que él ya no puede tocar. Sin ese
      // paso, podría sobrescribirlas después de que se aprueben.
      const urlTrasera = await storageService.uploadKYCDocument(uid, 'vehiculo_trasera', fotoTrasera!, null);
      const urlSelfie = await storageService.uploadProfilePhoto(uid, selfie!);

      await callAgregarVehiculo({
        tipo: tipo!,
        marca: marca.trim(),
        modelo: modelo.trim(),
        color: color.trim(),
        anio: anio.trim(),
        placa: revision.placa,
        fotoTrasera: urlTrasera,
        selfie: urlSelfie,
      });

      avisar(
        'Vehículo enviado',
        'Una persona de Go Safely va a comparar tu selfie con tu cédula y la placa con la foto. Te avisamos en cuanto esté aprobado; mientras tanto sigues conduciendo con el que ya tienes.'
      );
      router.replace('/mis-vehiculos' as any);
    } catch (e: any) {
      avisar('No se pudo enviar', mensajeDeError(e, 'Revisa tu conexión e inténtalo de nuevo.'));
    } finally {
      setEnviando(false);
    }
  };

  // ── La cámara a pantalla completa ────────────────────────────────────────
  if (camaraAbierta) {
    return (
      <View style={styles.camaraPantalla}>
        <CameraView style={RNStyleSheet.absoluteFill} facing="front" ref={(r) => setCamaraRef(r)} />
        <View style={styles.camaraPie}>
          <Text style={styles.camaraTexto}>
            Mira a la cámara. Esta foto se compara con tu cédula para confirmar que eres tú quien está
            cambiando el vehículo.
          </Text>
          <View style={styles.camaraBotones}>
            <TouchableOpacity onPress={() => setCamaraAbierta(false)} style={styles.camaraCancelar}>
              <Text style={styles.camaraCancelarTexto}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={tomarSelfie}
              style={styles.obturador}
              accessibilityRole="button"
              accessibilityLabel="Tomar la selfie"
            >
              {capturando ? <ActivityIndicator color="#0A2540" /> : <View style={styles.obturadorDentro} />}
            </TouchableOpacity>
            <View style={{ width: 84 }} />
          </View>
        </View>
      </View>
    );
  }

  return (
    <ScreenBackground>
      <PantallaSegura style={styles.safe}>
        <View style={[styles.cabecera, { borderBottomColor: colors.border }]}>
          <TouchableOpacity
            onPress={() => (router.canGoBack() ? router.back() : router.replace('/mis-vehiculos' as any))}
            style={styles.volver}
            accessibilityRole="button"
            accessibilityLabel="Volver"
          >
            <Ionicons name="arrow-back" size={20} color={colors.primary} />
            <Text style={[styles.volverTexto, { color: colors.primary }]}>Volver</Text>
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={styles.cuerpo} keyboardShouldPersistTaps="handled">
          <Text style={[styles.titulo, { color: colors.text }]}>Agregar un vehículo</Text>
          <Text style={[styles.ayuda, { color: colors.textSecondary }]}>
            Lo revisa una persona de Go Safely antes de que puedas conducirlo. Mientras tanto sigues
            trabajando con el que ya tienes.
          </Text>

          <Text style={[styles.etiqueta, { color: colors.text }]}>¿Qué vas a conducir?</Text>
          <View style={styles.tipos}>
            {TIPOS.map((t) => {
              const puesto = tipo === t.id;
              return (
                <TouchableOpacity
                  key={t.id}
                  onPress={() => setTipo(t.id)}
                  style={[
                    styles.tipo,
                    { borderColor: puesto ? colors.primary : colors.border, backgroundColor: colors.card },
                    puesto && { borderWidth: 2 },
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel={`${t.nombre}, cuota ${t.cuota}`}
                >
                  <Ionicons name={t.icono} size={22} color={puesto ? colors.primary : colors.textSecondary} />
                  <Text style={[styles.tipoNombre, { color: colors.text }]}>{t.nombre}</Text>
                  {/* La cuota se dice ANTES de elegir: de este dato sale lo que
                      paga al mes, y enterarse después se siente a engaño. */}
                  <Text style={[styles.tipoCuota, { color: colors.textSecondary }]}>{t.cuota}</Text>
                </TouchableOpacity>
              );
            })}
          </View>

          <Text style={[styles.etiqueta, { color: colors.text }]}>Marca</Text>
          <TextInput
            style={[styles.campo, { color: colors.text, borderColor: colors.border, backgroundColor: colors.card }]}
            placeholder="Ej. Toyota"
            placeholderTextColor={colors.textSecondary}
            value={marca}
            onChangeText={setMarca}
          />
          <Text style={[styles.etiqueta, { color: colors.text }]}>Modelo</Text>
          <TextInput
            style={[styles.campo, { color: colors.text, borderColor: colors.border, backgroundColor: colors.card }]}
            placeholder="Ej. Corolla"
            placeholderTextColor={colors.textSecondary}
            value={modelo}
            onChangeText={setModelo}
          />
          <Text style={[styles.etiqueta, { color: colors.text }]}>Color</Text>
          <TextInput
            style={[styles.campo, { color: colors.text, borderColor: colors.border, backgroundColor: colors.card }]}
            placeholder="Ej. blanco"
            placeholderTextColor={colors.textSecondary}
            value={color}
            onChangeText={setColor}
          />
          <Text style={[styles.etiqueta, { color: colors.text }]}>Año (opcional)</Text>
          <TextInput
            style={[styles.campo, { color: colors.text, borderColor: colors.border, backgroundColor: colors.card }]}
            placeholder="Ej. 2016"
            placeholderTextColor={colors.textSecondary}
            keyboardType="number-pad"
            maxLength={4}
            value={anio}
            onChangeText={setAnio}
          />
          <Text style={[styles.etiqueta, { color: colors.text }]}>Placa</Text>
          <TextInput
            style={[styles.campo, { color: colors.text, borderColor: colors.border, backgroundColor: colors.card }]}
            placeholder={esMoto ? 'Ej. K12345' : 'Ej. A123456'}
            placeholderTextColor={colors.textSecondary}
            autoCapitalize="characters"
            maxLength={9}
            value={placa}
            onChangeText={(x) => setPlaca(normalizarPlaca(x))}
          />
          <Text style={[styles.pista, { color: colors.textSecondary }]}>{ayudaPlaca(esMoto)}</Text>

          {/* ── LAS DOS FOTOS ─────────────────────────────────────────────── */}
          <Text style={[styles.etiqueta, { color: colors.text, marginTop: 20 }]}>
            Foto de la parte de atrás
          </Text>
          <Text style={[styles.pista, { color: colors.textSecondary, marginTop: 0 }]}>
            Tiene que verse el color del vehículo y la placa completa, legible. Es lo que tu pasajero va a
            comparar en la acera.
          </Text>
          {fotoTrasera ? (
            <Image source={{ uri: fotoTrasera }} style={styles.vistaPrevia} resizeMode="contain" />
          ) : null}
          <TouchableOpacity
            onPress={tomarFotoTrasera}
            style={[styles.botonFoto, { borderColor: colors.primary }]}
            accessibilityRole="button"
            accessibilityLabel="Tomar la foto de la parte de atrás del vehículo"
          >
            <Ionicons name="camera" size={18} color={colors.primary} />
            <Text style={[styles.botonFotoTexto, { color: colors.primary }]}>
              {fotoTrasera ? 'Tomar otra' : 'Abrir cámara'}
            </Text>
          </TouchableOpacity>

          <Text style={[styles.etiqueta, { color: colors.text, marginTop: 20 }]}>Tu selfie, ahora mismo</Text>
          <Text style={[styles.pista, { color: colors.textSecondary, marginTop: 0 }]}>
            Se compara con tu cédula. Así nadie puede cambiar el vehículo de tu cuenta sin ser tú.
          </Text>
          {selfie ? <Image source={{ uri: selfie }} style={styles.vistaPreviaSelfie} resizeMode="cover" /> : null}
          <TouchableOpacity
            onPress={abrirCamara}
            style={[styles.botonFoto, { borderColor: colors.primary }]}
            accessibilityRole="button"
            accessibilityLabel="Tomar tu selfie en vivo"
          >
            <Ionicons name="person-circle-outline" size={18} color={colors.primary} />
            <Text style={[styles.botonFotoTexto, { color: colors.primary }]}>
              {selfie ? 'Tomar otra' : 'Abrir cámara'}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={enviar}
            disabled={!puedeEnviar || enviando}
            style={[
              styles.enviar,
              { backgroundColor: colors.primary },
              (!puedeEnviar || enviando) && { opacity: 0.5 },
            ]}
            accessibilityRole="button"
            accessibilityLabel="Enviar el vehículo a revisión"
          >
            <Text style={styles.enviarTexto}>{enviando ? 'Enviando…' : 'Enviar a revisión'}</Text>
          </TouchableOpacity>
          {!puedeEnviar && (
            // Un botón apagado sin decir qué falta es el mismo callejón sin
            // salida de siempre: el usuario lo toca, no pasa nada, y se va.
            <Text style={[styles.pista, { color: colors.textSecondary }]}>
              Faltan:{' '}
              {[
                !tipo && 'el tipo de vehículo',
                !marca.trim() && 'la marca',
                !modelo.trim() && 'el modelo',
                !color.trim() && 'el color',
                !placa && 'la placa',
                !fotoTrasera && 'la foto de atrás',
                !selfie && 'tu selfie',
              ]
                .filter(Boolean)
                .join(', ')}
              .
            </Text>
          )}
          <View style={{ height: 40 }} />
        </ScrollView>
      </PantallaSegura>
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  cabecera: { paddingHorizontal: 16, paddingTop: 8, paddingBottom: 8, borderBottomWidth: 1 },
  volver: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(33, 150, 243, 0.08)',
  },
  volverTexto: { fontSize: 14, fontWeight: '600' },
  cuerpo: { padding: 16, paddingBottom: 40 },
  titulo: { fontSize: 24, fontWeight: 'bold', marginBottom: 6 },
  ayuda: { fontSize: 13, lineHeight: 19, marginBottom: 18 },
  etiqueta: { fontSize: 13, fontWeight: '700', marginTop: 14, marginBottom: 6 },
  campo: { borderWidth: 1, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11, fontSize: 15 },
  pista: { fontSize: 11.5, lineHeight: 16, marginTop: 6 },
  tipos: { flexDirection: 'row', gap: 10, flexWrap: 'wrap' },
  tipo: { flex: 1, minWidth: 96, alignItems: 'center', gap: 4, borderWidth: 1, borderRadius: 12, paddingVertical: 12, paddingHorizontal: 8 },
  tipoNombre: { fontSize: 13.5, fontWeight: '700' },
  tipoCuota: { fontSize: 10.5, textAlign: 'center' },
  vistaPrevia: { width: '100%', aspectRatio: 4 / 3, borderRadius: 12, marginTop: 10, backgroundColor: 'rgba(17,24,39,0.10)' },
  vistaPreviaSelfie: { width: 120, height: 160, borderRadius: 12, marginTop: 10, alignSelf: 'center', backgroundColor: 'rgba(17,24,39,0.10)' },
  botonFoto: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1.5,
    borderRadius: 10,
    paddingVertical: 12,
    marginTop: 10,
  },
  botonFotoTexto: { fontSize: 14, fontWeight: '700' },
  enviar: { borderRadius: 12, paddingVertical: 15, alignItems: 'center', marginTop: 24 },
  enviarTexto: { color: '#FFF', fontSize: 15.5, fontWeight: '800' },
  // ── Cámara ──────────────────────────────────────────────────────────────
  camaraPantalla: { flex: 1, backgroundColor: '#000' },
  camaraPie: { position: 'absolute', left: 0, right: 0, bottom: 0, padding: 22, paddingBottom: 38, backgroundColor: 'rgba(10,37,64,0.82)' },
  camaraTexto: { color: '#FFF', fontSize: 13, lineHeight: 18, textAlign: 'center', marginBottom: 18 },
  camaraBotones: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  camaraCancelar: { width: 84, paddingVertical: 10 },
  camaraCancelarTexto: { color: '#FFF', fontSize: 14, fontWeight: '600' },
  obturador: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: '#FFF',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 4,
    borderColor: 'rgba(255,255,255,0.45)',
  },
  obturadorDentro: { width: 56, height: 56, borderRadius: 28, backgroundColor: '#FFF' },
});
